import config from 'constants/config'

export interface PassportCardQuoteParams {
  travellersDetails: Array<App.PassportCardTravellerDetail>
  destinationCountries: Array<string>
  tripStartDate: string
  tripEndDate: string
  productId: string
}

export interface PassportCardQuoteRequestParams extends PassportCardQuoteParams {
  totalPrice: number
  countryCode: string
  currency: string
  items: Array<App.PassportCardQuoteItem>
  filters?: App.PassportCardQuotePayload['filters']
  loyaltyTier?: string
}

export function hasRequiredPassportCardQuoteRequestFields(
  requestParams: PassportCardQuoteRequestParams | undefined,
): requestParams is PassportCardQuoteRequestParams {
  if (!requestParams) return false

  return (
    requestParams.travellersDetails.length > 0 &&
    requestParams.travellersDetails.every(traveller => !!traveller.dateOfBirth) &&
    requestParams.destinationCountries.length > 0 &&
    !!requestParams.tripStartDate &&
    !!requestParams.tripEndDate &&
    requestParams.totalPrice > 0 &&
    !!requestParams.productId
  )
}

export function toPassportCardQuotePayload(
  requestParams: PassportCardQuoteRequestParams,
): App.PassportCardQuotePayload {
  return {
    brand: config.BRAND,
    country_code: requestParams.countryCode,
    currency: requestParams.currency,
    trip_start_date: requestParams.tripStartDate,
    trip_end_date: requestParams.tripEndDate,
    travellers_details_v2: requestParams.travellersDetails.map(t => ({
      type: t.type === 'adult' ? 'adult' : 'children',
      date_of_birth: t.dateOfBirth,
    })),
    destination_countries: requestParams.destinationCountries,
    provider: 'passport_card',
    total_price: requestParams.totalPrice,
    product_id: requestParams.productId,
    items: requestParams.items.map(item => ({ ...item })),
    ...(requestParams.filters ? { filters: requestParams.filters } : {}),
    ...(requestParams.loyaltyTier ? { loyalty_tier: requestParams.loyaltyTier } : {}),
  }
}

export function travellerDetailsFromInsuranceTravellers(
  travellers: Array<App.PassportCardInsuranceTraveller>,
): Array<App.PassportCardTravellerDetail> {
  return travellers.map(traveller => ({
    type: traveller.ageRange,
    dateOfBirth: traveller.dateOfBirth || '',
  }))
}

export function travellerDetailsFromOccupants(occupants: App.Occupants): Array<App.PassportCardTravellerDetail> {
  const adults = Array.from(
    { length: occupants.adults || 0 },
    () => ({ type: 'adult' as const, dateOfBirth: '' }),
  )
  const childrenCount = (occupants.children || 0) + (occupants.infants || 0)
  const children = Array.from(
    { length: childrenCount },
    () => ({ type: 'child' as const, dateOfBirth: '' }),
  )

  return [...adults, ...children]
}
